/**
 * Transfers List Page - Clara Vale V2
 *
 * Compact layout mirroring Registrations: ListPageHeader (title + subtitle
 * + time filter) + single-row ListPageToolbar with status pills, saved
 * views, column toggle, search, and export. All shared components - no
 * bespoke header/toolbar/filter code.
 */

import { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/admin/components/app-shell'
import {
  useTransfers,
  useTransferStatusCounts,
  type Transfer,
} from '@/admin/hooks/use-transfers'
import { useTableState } from '@/admin/hooks/use-table-state'
import { useTableFilterPreferences } from '@/admin/hooks/use-table-filter-preferences'
import { useTableColumnPreferences } from '@/admin/hooks/use-table-column-preferences'
import { useDismissedStatusPills } from '@/admin/hooks/use-dismissed-status-pills'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { ArrowLeftRight } from 'lucide-react'
import {
  DataTable,
  type DataTableColumn,
} from '@/admin/components/data-table'
import {
  timeRangeSlice,
  toSavedTimeRange,
  useSavedListView,
} from '@/admin/hooks/use-saved-list-view'
import { ListPageToolbar } from '@/admin/components/list-page-toolbar'
import { ListPageHeader } from '@/admin/components/shared/list-page-header'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { NotificationDot } from '@/shared/components/notifications/notification-dot'
import { useTableExport } from '@/admin/hooks/use-table-export'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { useFormatters } from '@/shared/lib/formatting'
import type { StatusPillItem } from '@/admin/components/shared/status-pills'
import type { TimeRange } from '@/admin/components/shared/time-filter'
import { timeRangeToParams } from '@/admin/lib/time-range-params'

const TABLE_ID = 'transfers'

/** Only the reference column is structural; everything else is hideable. */
const LOCK_CANDIDATE_IDS = ['new_owner']

/** Format a local Date as YYYY-MM-DD without the UTC shift that
 *  ``toISOString()`` introduces for negative-offset timezones (a US user's
 *  local "today" would otherwise serialize as tomorrow, or a boundary date
 *  as the previous day). */
export function TransfersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const [search, setSearch] = useState('')
  // A link may open the list on one status (Settings > Reminders "View transfers").
  const [searchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState<string | null>(
    () => searchParams.get('status'),
  )
  const [timeRange, setTimeRange] = useState<TimeRange>('all_time')
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | undefined>()

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnVisibility,
    saveColumnOrder,
    applyDefaultColumnPreferences,
  } = useTableColumnPreferences(TABLE_ID)
  const { dismissedKeys: dismissedStatusKeys, dismiss: dismissStatusPill } =
    useDismissedStatusPills(TABLE_ID)

  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState(TABLE_ID)
  // Filters panel selection, persisted per tenant+table; a link such as
  // ``/transfers?group=any`` preselects the HOA facet.
  const [filterValues, setFilterValues] = useTableFilterPreferences(TABLE_ID, {
    seedFromUrl: ['group'],
  })

  // Views
  const debouncedSearch = useDebouncedValue(search)

  // Params shared by the list and the status-counts endpoints. The counts
  // endpoint ignores ``status`` so clicking a pill filters the table
  // without collapsing the pill bar.
  const paramsForCounts = useMemo(() => {
    const p: Record<string, string> = { ...timeRangeToParams(timeRange, customRange) }
    if (debouncedSearch) p.search = debouncedSearch
    // Filters panel: each facet's values comma-joined (the server ORs them).
    for (const [key, values] of Object.entries(filterValues)) {
      if (values.length) p[key] = values.join(',')
    }
    return p
  }, [debouncedSearch, timeRange, customRange, filterValues])

  const params = useMemo(() => {
    const p: Record<string, string> = { ...queryParams, ...paramsForCounts }
    if (statusFilter) p.status = statusFilter
    return p
  }, [queryParams, paramsForCounts, statusFilter])

  const { data, isLoading, isFetching, isError, refetch } = useTransfers(params, { keepPreviousPage: true })

  const results = useMemo(() => data?.results ?? [], [data])
  const totalCount = data?.count ?? 0

  const { data: statusCounts = [] } = useTransferStatusCounts(paramsForCounts)
  const statusPills: StatusPillItem[] = useMemo(
    () =>
      statusCounts.map((s) => ({ key: s.key, label: s.label, count: s.count })),
    [statusCounts],
  )
  const needsReviewCount = statusCounts.find((s) => s.key === 'pending_review')?.count

  const columns: DataTableColumn<Transfer>[] = useMemo(
    () => [
      {
        id: 'new_owner',
        header: 'New Owner',
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5">
            {row.has_unread_activity && (
              <NotificationDot level="attention" title="Unread activity" />
            )}
            {`${row.new_owner_first_name} ${row.new_owner_last_name}`.trim() || '-'}
          </span>
        ),
        sortable: true,
        sortField: 'new_owner_last_name',
        sortKind: 'text',
      },
      {
        // Right after the owner, so it is in view without scrolling, the same
        // on every list page.
        id: 'updated_at',
        header: t('common.lastActivity', 'Last Activity'),
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink-2)]">{formatDate(row.updated_at)}</span>
        ),
        sortable: true,
        sortField: 'updated_at',
        sortKind: 'date',
      },
      {
        id: 'property',
        header: 'Property',
        cell: (row) => row.host_address?.display || '-',
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <StatusBadge status={row.status_label ?? row.status} />,
        sortable: true,
        // No sortKind: it sorts by status code, not the label shown.
        sortField: 'status',
      },
      {
        id: 'transfer_type',
        header: 'Transfer Type',
        cell: (row) => row.transfer_type?.label ?? '-',
        sortable: true,
        sortField: 'transfer_type__code',
        sortKind: 'text',
      },
      {
        id: 'ownership_change_date',
        header: 'Ownership Change',
        cell: (row) =>
          row.ownership_change_date ? formatDate(row.ownership_change_date) : '-',
        sortable: true,
        sortField: 'ownership_change_date',
        sortKind: 'date',
      },
    ],
    [formatDate, t],
  )

  const { exportCsv, isExporting } = useTableExport<Transfer>({
    endpoint: '/v1/transfers/',
    columns,
    columnVisibility,
    params,
  })

  const alwaysShownColumns = useMemo(() => {
    return LOCK_CANDIDATE_IDS.filter((id) => {
      const col = columns.find((c) => c.id === id)
      if (!col) return false
      if (id in columnVisibility) return columnVisibility[id] !== false
      return col.defaultVisible !== false
    })
  }, [columns, columnVisibility])

  // Saved views hold columns, order, sort, the status pill and time range.
  const savedView = useSavedListView({
    tableId: TABLE_ID,
    columns,
    columnVisibility,
    setColumnVisibility,
    saveColumnVisibility,
    applyDefaultColumnPreferences,
    columnOrder,
    setColumnOrder,
    saveColumnOrder,
    lockedColumnIds: LOCK_CANDIDATE_IDS,
    slices: {
      sorting: { value: sorting ?? null, apply: setSorting, empty: null },
      status: { value: statusFilter, apply: setStatusFilter, empty: null },
      timeRange: timeRangeSlice(timeRange, customRange, setTimeRange, setCustomRange),
    },
  })

  const handleTimeFilterChange = (range: TimeRange, custom?: { from: string; to: string }) => {
    savedView.change('timeRange', toSavedTimeRange(range, custom))
    setPage(1)
  }

  return (
    <AppShell>
      <main className="page-container py-10">
        <ListPageHeader
          title="Transfers"
          totalCount={totalCount}
          highlightStat={
            needsReviewCount ? { label: 'need review', value: needsReviewCount } : undefined
          }
          timeFilterValue={timeRange}
          timeFilterCustomRange={customRange}
          onTimeFilterChange={handleTimeFilterChange}
          actionButton={{
            label: 'New Transfer',
            onClick: () => navigate('/transfers/new'),
            dataTour: 'transfers-list',
          }}
        />

        <ListPageToolbar<Transfer>
          layout="compact"
          tableId={TABLE_ID}
          search={search}
          onSearchChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          searchPlaceholder="Search transfers..."
          statusPills={statusPills}
          activeStatusKey={statusFilter}
          onStatusSelect={(key) => {
            savedView.change('status', key)
            setPage(1)
          }}
          statusTotalCount={totalCount}
          dismissedStatusKeys={dismissedStatusKeys}
          onStatusDismiss={(key) => {
            dismissStatusPill(key)
            if (statusFilter === key) {
              savedView.change('status', null)
              setPage(1)
            }
          }}
          {...savedView.toolbarProps}
          columns={columns}
          activeFilters={[]}
          onFiltersChange={() => {}}
          showColumnFilter={false}
          filterFacets={data?.filter_options ?? []}
          filterValues={filterValues}
          onFilterValuesChange={(values) => {
            setFilterValues(values)
            setPage(1)
          }}
          columnVisibility={columnVisibility}
          alwaysShownColumns={alwaysShownColumns}
          columnOrder={columnOrder}
          onExport={exportCsv}
          isExporting={isExporting}
        />

        <div data-tour="transfer-list">
          <DataTable<Transfer>
            tableId={TABLE_ID}
            columns={columns}
            data={results}
            totalCount={totalCount}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={refetch}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sorting={sorting}
            columnOrder={columnOrder}
            onSortingChange={(next) => savedView.change('sorting', next)}
            onRowClick={(row) => navigate(`/transfers/${row.uuid}`)}
            emptyStateNode={
              <EmptyState
                icon={ArrowLeftRight}
                title={t('transfers.emptyTitle', 'No transfers yet')}
                description={t(
                  'transfers.emptyDescription',
                  'Warranty ownership transfers will appear here.',
                )}
              />
            }
            columnVisibility={columnVisibility}
            highlightQuery={debouncedSearch}
            rowKey="uuid"
          />
        </div>
      </main>
    </AppShell>
  )
}
